import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  TableSkeleton,
  formatDate,
  useToast,
} from '@mgdlead/ui';
import { Crown, MailX, Trash2, UserPlus, Users } from 'lucide-react';
import { can, canManageMember, canTransferOwnership } from '@mgdlead/shared';
import type { WorkspaceRole } from '@mgdlead/shared';

import { ConfirmByNameDialog } from '../../../components/ConfirmByNameDialog';
import { FormAlert } from '@mgdlead/ui';
import { NativeSelect } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { useSession } from '../../../auth/useSession';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import {
  ASSIGNABLE_ROLES,
  isAssignableRole,
  useInviteMember,
  useMembers,
  useRemoveMember,
  useRevokeInvite,
  useTransferOwnership,
  useUpdateMemberRole,
} from '../../../lib/api';
import type { AssignableRole, Invitation, Member, SeatUsage } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';
import { inviteMemberSchema } from '../../../lib/schemas';
import type { InviteMemberValues } from '../../../lib/schemas';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { useZodForm } from '../../../lib/useZodForm';

const ROLE_LABEL: Record<WorkspaceRole, string> = {
  OWNER: 'Proprietário',
  ADMIN: 'Administrador',
  ANALYST: 'Analista',
  MEMBER: 'Membro',
};

const ROLE_HINT: Record<AssignableRole, string> = {
  ADMIN: 'Opera o produto inteiro, menos dinheiro e exclusão do workspace.',
  ANALYST: 'Cuida do dia a dia: cria campanha, exporta e reconhece incidente.',
  MEMBER: 'Vê a operação e não muda nada.',
};

/** Nome de exibição — nunca vazio, senão a confirmação forte fica sem alvo. */
function memberLabel(member: Member): string {
  return member.name.trim().length > 0 ? member.name : member.email;
}

export function MembersPage() {
  const { workspace, role } = useWorkspace();
  const { user } = useSession();
  const { toast } = useToast();
  useDocumentTitle('Membros');

  const membersQuery = useMembers(workspace.id);
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const transferOwnership = useTransferOwnership();

  const [removing, setRemoving] = useState<Member | null>(null);
  const [transferring, setTransferring] = useState<Member | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleRoleChange = useCallback(
    (member: Member, nextRole: AssignableRole) => {
      setRowError(null);
      updateRole.mutate(
        { workspaceId: workspace.id, userId: member.userId, role: nextRole },
        {
          onSuccess: () =>
            toast({
              title: 'Papel atualizado',
              description: `${member.email} agora e ${ROLE_LABEL[nextRole]}.`,
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
    [toast, updateRole, workspace.id],
  );

  const data = membersQuery.data;

  return (
    <>
      <PageHeader
        title="Membros"
        description={`Quem tem acesso a ${workspace.name} e com qual papel.`}
        action={
          <Link
            to={workspacePath(workspace.slug, 'configuracoes')}
            className="inline-flex h-9 w-full items-center justify-center rounded-md border border-border bg-surface px-3 text-sm font-medium hover:border-border-strong hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-11 md:w-auto"
          >
            Voltar para configurações
          </Link>
        }
      />

      {can(role, 'member:invite') ? <InviteSection seats={data?.seats} /> : null}

      {rowError ? <FormAlert className="mb-3">{rowError}</FormAlert> : null}

      <Card padded={false}>
        <div className="p-3 md:p-4">
          <CardHeader>
            <CardTitle>Pessoas com acesso</CardTitle>
            <CardDescription>
              Existe exatamente um proprietário. Para trocar, use transferir propriedade.
            </CardDescription>
          </CardHeader>
        </div>

        {membersQuery.isPending ? (
          <div className="px-3 pb-3 md:px-4 md:pb-4">
            <TableSkeleton rows={4} columns={5} />
          </div>
        ) : membersQuery.isError ? (
          <ErrorState
            title="Não conseguimos carregar os membros"
            description={humanizeError(membersQuery.error)}
            onRetry={() => void membersQuery.refetch()}
          />
        ) : (data?.members.length ?? 0) === 0 ? (
          <EmptyState
            kind="no-data"
            icon={Users}
            title="Nenhum membro listado"
            description="Isso não deveria acontecer: todo workspace tem ao menos o proprietário. Recarregue a página."
          />
        ) : (
          <Table caption={`Membros de ${workspace.name}`}>
            <TableHeader>
              <TableRow>
                <TableHead>Pessoa</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Desde</TableHead>
                <TableHead sticky="right">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.members ?? []).map((member) => {
                const isSelf = user !== null && member.userId === user.id;
                const manage = canManageMember({
                  actorRole: role,
                  targetRole: member.role,
                  actorIsSelf: isSelf,
                });
                const canRemove =
                  can(role, 'member:remove') &&
                  manage.allowed &&
                  !isSelf &&
                  member.role !== 'OWNER';
                const canTransfer = canTransferOwnership(role, member.role) && !isSelf;
                // OWNER nunca entra no seletor; `manage.allowed` já o exclui
                const editableRole = manage.allowed && isAssignableRole(member.role);

                return (
                  <TableRow key={member.membershipId}>
                    <TableRowHeader>
                      <span className="block truncate">{memberLabel(member)}</span>
                      {member.name.trim().length > 0 ? (
                        <span className="block truncate text-2xs text-muted-foreground">
                          {member.email}
                        </span>
                      ) : null}
                    </TableRowHeader>

                    <TableCell>
                      {editableRole ? (
                        <>
                          <label className="sr-only" htmlFor={`role-${member.userId}`}>
                            Papel de {member.email}
                          </label>
                          <NativeSelect
                            id={`role-${member.userId}`}
                            size="sm"
                            value={member.role}
                            disabled={updateRole.isPending}
                            onChange={(event) => {
                              const next = event.target.value;
                              if (isAssignableRole(next)) handleRoleChange(member, next);
                            }}
                          >
                            {ASSIGNABLE_ROLES.map((option) => (
                              <option key={option} value={option}>
                                {ROLE_LABEL[option]}
                              </option>
                            ))}
                          </NativeSelect>
                        </>
                      ) : (
                        /* sem permissão: o papel aparece, a ação não existe */
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          {member.role === 'OWNER' ? (
                            <Crown aria-hidden="true" className="size-3.5 text-muted-foreground" />
                          ) : null}
                          {ROLE_LABEL[member.role]}
                        </span>
                      )}
                    </TableCell>

                    <TableCell nowrap>
                      <Badge tone={member.acceptedAt === null ? 'warning' : 'neutral'}>
                        {member.acceptedAt === null ? 'Não confirmado' : 'Ativo'}
                      </Badge>
                    </TableCell>

                    <TableCell nowrap>
                      {formatDate(member.createdAt, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        // o dia é sempre o do workspace (invariante 10)
                        timeZone: workspace.timezone,
                      })}
                    </TableCell>

                    <TableCell sticky="right" nowrap>
                      <div className="flex items-center justify-end gap-1">
                        {canTransfer ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            leadingIcon={<Crown />}
                            onClick={() => setTransferring(member)}
                          >
                            Transferir
                          </Button>
                        ) : null}
                        {canRemove ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Remover ${member.email}`}
                            leadingIcon={<Trash2 />}
                            onClick={() => setRemoving(member)}
                          >
                            Remover
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <PendingInvitations invitations={data?.invitations} loading={membersQuery.isPending} />

      {/* Confirmação forte: só estas duas ações a merecem. */}
      <ConfirmByNameDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remover membro"
        description={
          <>
            {removing ? memberLabel(removing) : ''} perde o acesso a {workspace.name} imediatamente.
            O histórico de ações dessa pessoa continua no registro.
          </>
        }
        confirmationText={removing ? memberLabel(removing) : ''}
        confirmLabel="Remover do workspace"
        onConfirm={async () => {
          if (!removing) return;
          await removeMember.mutateAsync({ workspaceId: workspace.id, userId: removing.userId });
          toast({ title: 'Membro removido', tone: 'success' });
          setRemoving(null);
        }}
      />

      <ConfirmByNameDialog
        open={transferring !== null}
        onOpenChange={(open) => {
          if (!open) setTransferring(null);
        }}
        title="Transferir propriedade"
        description={
          <>
            {transferring ? memberLabel(transferring) : ''} passa a ser o proprietário de{' '}
            {workspace.name} e você vira administrador. Isso inclui o controle da assinatura e não
            tem desfazer — só o novo proprietário pode devolver.
          </>
        }
        confirmationText={transferring ? memberLabel(transferring) : ''}
        confirmLabel="Transferir propriedade"
        onConfirm={async () => {
          if (!transferring) return;
          await transferOwnership.mutateAsync({
            workspaceId: workspace.id,
            userId: transferring.userId,
          });
          toast({ title: 'Propriedade transferida', tone: 'success' });
          setTransferring(null);
        }}
      />
    </>
  );
}

/* ========================================================================== */
/*  Convites pendentes                                                         */
/* ========================================================================== */

interface PendingInvitationsProps {
  invitations: readonly Invitation[] | undefined;
  loading: boolean;
}

/**
 * Convite aberto NÃO é membro: quem foi convidado ainda não tem acesso, e
 * misturar os dois na mesma tabela faz o dono achar que já tem gente dentro.
 */
function PendingInvitations({ invitations, loading }: PendingInvitationsProps) {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  const revokeInvite = useRevokeInvite();
  const [error, setError] = useState<string | null>(null);

  if (loading) return null;
  if (!invitations || invitations.length === 0) return null;
  if (!can(role, 'member:read')) return null;

  return (
    <Card padded={false} className="mt-3">
      <div className="p-3 md:p-4">
        <CardHeader>
          <CardTitle>Convites pendentes</CardTitle>
          <CardDescription>
            Ainda não contam como acesso: o convite só vira membro quando a pessoa aceita.
          </CardDescription>
        </CardHeader>
        {error ? <FormAlert>{error}</FormAlert> : null}
      </div>

      <Table caption="Convites em aberto">
        <TableHeader>
          <TableRow>
            <TableHead>E-mail</TableHead>
            <TableHead>Papel</TableHead>
            <TableHead>Expira em</TableHead>
            <TableHead sticky="right">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableRowHeader>{invitation.email}</TableRowHeader>
              <TableCell>{ROLE_LABEL[invitation.role]}</TableCell>
              <TableCell nowrap>
                {formatDate(invitation.expiresAt, {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: workspace.timezone,
                })}
              </TableCell>
              <TableCell sticky="right" nowrap>
                {can(role, 'member:invite') ? (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<MailX />}
                      loading={revokeInvite.isPending}
                      onClick={() => {
                        setError(null);
                        revokeInvite.mutate(
                          { workspaceId: workspace.id, invitationId: invitation.id },
                          {
                            onSuccess: () => toast({ title: 'Convite revogado', tone: 'success' }),
                            onError: (cause: unknown) => setError(humanizeError(cause)),
                          },
                        );
                      }}
                    >
                      Revogar
                    </Button>
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ========================================================================== */
/*  Convite                                                                    */
/* ========================================================================== */

const INVITE_INITIAL: InviteMemberValues = { email: '', role: 'MEMBER' };

function InviteSection({ seats }: { seats: SeatUsage | undefined }) {
  const { workspace } = useWorkspace();
  const { toast } = useToast();
  const inviteMember = useInviteMember();

  const onSubmit = useCallback(
    async (values: { email: string; role: AssignableRole }) => {
      await inviteMember.mutateAsync({ workspaceId: workspace.id, ...values });
      toast({
        title: 'Convite enviado',
        description: `${values.email} recebeu o link de acesso.`,
        tone: 'success',
      });
      // `form` é declarado abaixo: o acesso acontece dentro do callback, já
      // depois da montagem — não há uso antes da declaração.
      form.reset(INVITE_INITIAL);
    },
    [inviteMember, toast, workspace.id],
  );

  const form = useZodForm({
    schema: inviteMemberSchema,
    initialValues: INVITE_INITIAL,
    onSubmit,
  });

  const selected = form.values.role;
  // o limite vem AVALIADO do servidor; o front não faz conta de plano
  const seatsFull = seats !== undefined && !seats.unlimited && !seats.allowed;

  return (
    <Card className="mb-3 flex flex-col gap-4">
      <CardHeader>
        <CardTitle>Convidar alguém</CardTitle>
        <CardDescription>
          O convite chega por e-mail. O acesso só vale depois que a pessoa aceita.
        </CardDescription>
      </CardHeader>

      {seatsFull ? (
        <FormAlert tone="info">
          Seu plano permite {seats?.limit} pessoas e você já está em {seats?.current}. Libere um
          assento ou mude de plano antes de convidar.
        </FormAlert>
      ) : null}

      <form
        noValidate
        onSubmit={form.handleSubmit}
        className="flex flex-col gap-3 md:flex-row md:items-start"
      >
        <Field label="E-mail" error={form.errors.email} required className="md:flex-1">
          {(control) => (
            <Input
              {...control}
              type="email"
              name="email"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="pessoa@empresa.com"
              value={form.values.email}
              invalid={form.errors.email !== undefined}
              onChange={(event) => form.setField('email', event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Papel"
          hint={ROLE_HINT[selected]}
          error={form.errors.role}
          required
          className="md:w-64"
        >
          {(control) => (
            <NativeSelect
              {...control}
              name="role"
              value={selected}
              invalid={form.errors.role !== undefined}
              onChange={(event) => {
                const next = event.target.value;
                if (isAssignableRole(next)) form.setField('role', next);
              }}
            >
              {ASSIGNABLE_ROLES.map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABEL[option]}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          block
          leadingIcon={<UserPlus />}
          loading={form.pending}
          loadingLabel="Enviando convite"
          disabled={seatsFull}
          className="md:mt-[1.625rem] md:w-auto"
        >
          Convidar
        </Button>
      </form>

      {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}
    </Card>
  );
}
